// Share code: shows the current `AOD2-…` code with a Copy button, and takes
// a pasted code to restore its ruleset (seats fall back to the base preset).

import { Button } from '@aod/ui/components/ui/button';
import { Input } from '@aod/ui/components/ui/input';
import { Check, ClipboardCopy, Share2 } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { type MessageKey, useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import { type SetupDraft, draftFromRuleset } from './setupModel.js';
import { decodeShareCode, encodeShareCode } from './shareCode.js';

interface Props {
  readonly draft: SetupDraft;
  readonly onLoad: (draft: SetupDraft) => void;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function ShareCodePanel({ draft, onLoad }: Props) {
  const t = useT();
  const id = useId();
  const code = encodeShareCode(draft.ruleset, draft.basedOn);
  const [copied, setCopied] = useState(false);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState<{ ok: boolean; key: MessageKey } | null>(null);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const apply = () => {
    if (!input.trim()) return;
    const res = decodeShareCode(input);
    if (res.ok) {
      onLoad(draftFromRuleset(res.value.ruleset, res.value.basedOn));
      setStatus({ ok: true, key: 'setup.share.applied' });
      setInput('');
    } else {
      setStatus({ ok: false, key: `setup.share.error.${res.error}` });
    }
  };

  return (
    <section
      aria-labelledby={`${id}-title`}
      className="grid gap-3 rounded-xl border border-border bg-card/70 p-3 sm:p-4 md:grid-cols-2"
    >
      <div className="flex min-w-0 flex-col gap-1.5">
        <h2
          id={`${id}-title`}
          className="flex items-center gap-1.5 text-sm font-semibold text-foreground"
        >
          <Share2 className="size-4 text-primary" aria-hidden />
          <label htmlFor={`${id}-code`}>{t('setup.share.label')}</label>
        </h2>
        <div className="flex gap-2">
          <Input
            id={`${id}-code`}
            readOnly
            value={code}
            onFocus={(e) => e.currentTarget.select()}
            aria-describedby={`${id}-hint`}
            data-testid="setup-share-code"
            {...appDom({ id: 'setup-share-code', type: 'Text', role: 'text', scope: 'main' })}
            className="min-w-0 flex-1 font-mono text-sm tracking-wider"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t('setup.share.copy')}
            title={t('setup.share.copy')}
            onClick={() => {
              void copyText(code).then(setCopied);
            }}
          >
            {copied ? (
              <Check className="text-primary" aria-hidden />
            ) : (
              <ClipboardCopy aria-hidden />
            )}
          </Button>
        </div>
        <p id={`${id}-hint`} className="text-xs text-muted-foreground" aria-live="polite">
          {copied ? t('setup.share.copied') : t('setup.share.hint')}
        </p>
      </div>
      <form
        className="flex min-w-0 flex-col gap-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          apply();
        }}
      >
        <label htmlFor={`${id}-paste`} className="text-sm font-semibold text-foreground">
          {t('setup.share.input')}
        </label>
        <div className="flex gap-2">
          <Input
            id={`${id}-paste`}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setStatus(null);
            }}
            placeholder="AOD2-…"
            autoComplete="off"
            spellCheck={false}
            aria-invalid={status?.ok === false || undefined}
            aria-describedby={`${id}-status`}
            className="min-w-0 flex-1 font-mono text-sm uppercase tracking-wider"
          />
          <Button type="submit" variant="secondary" disabled={!input.trim()}>
            {t('setup.share.apply')}
          </Button>
        </div>
        <p
          id={`${id}-status`}
          aria-live="polite"
          className={
            status?.ok === false ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'
          }
        >
          {status ? t(status.key) : ' '}
        </p>
      </form>
    </section>
  );
}
